// ==============================
// GreetingAudioUnlock — Task #519
// ==============================
// The /lab port of the /learn stage's audio-unlock kit (Task #515). The old
// Safari "May <legend> read their first words to you?" permission card
// (SafariGreetingPopup, Task #199) is RETIRED — opening a character just
// speaks, on every browser, through the same three paths as /learn:
//
//   auto          — a bounded probe shows the browser permits audible
//                   autoplay → the greeting speaks immediately (Chrome and
//                   any browser with media-engagement trust is unchanged).
//   early-gesture — ANY tap/click/keypress since the bundle executed is
//                   harvested here (capture phase, userActivation-verified)
//                   and, inside that gesture's stack, resumes the realtime
//                   TTS AudioContext and plays the REAL silent clip. When
//                   the greeting later starts it is already audible. The
//                   picker-close tap, picking a legend, or any click during
//                   the long model load is usually this gesture.
//   tap           — playback refused with ZERO interaction (a truly cold,
//                   untouched Safari page — the one honest browser-policy
//                   limit): the greeting is HELD (never burned as text), a
//                   minimal on-theme pill shows, and the first tap anywhere
//                   starts the voice. The pill is a beat in the flow, not a
//                   permission card, and leaves as soon as speech commits.
//
// Two hard rules ported verbatim from the /learn kit:
//   - The silent clip must be a REAL WAV. Playing an empty `new Audio()`
//     grants nothing on Safari/iOS — the unlock only sticks when actual
//     media plays inside the gesture.
//   - Safari blesses audio PER-ELEMENT. The Web-Audio chat path only needs
//     its AudioContext resumed, but the GreetingService <audio> path needs
//     the SAME element blessed in the gesture that is later played — hence
//     the persistent greeting element below (mirrors the /learn narration
//     element contract).
//
// This kit only resumes the audio context and blesses elements with the
// silent clip. It NEVER touches the sacred audio pipeline internals (A2F
// driver, never-pause-the-speaker rules), and it can never cut live speech:
// the harvest listeners tear down when the greeting commits, and blessing
// skips entirely while a real greeting has claimed the element.

// Tiny valid silence (real WAV data). Same clip as the /learn stage.
export const SILENT_AUDIO_URI =
    "data:audio/wav;base64,UklGRjQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YRAAAACAgICAgICAgICAgICAgICA";

let _installed = false;
let _committed = false;
let _gestureSeen = false;
let _armed = null;
let _pillEl = null;
let _blessedEl = null;
let _blessedElClaimed = false;

/** Locate the realtime-TTS engine wherever this page exposes it. */
function _findTTSEngine() {
    try {
        return (
            window.realtimeTTS ||
            window.tts ||
            (window.chatController &&
                (window.chatController.realtimeTTS ||
                    window.chatController.tts)) ||
            null
        );
    } catch (_) {
        return null;
    }
}

/**
 * The ONE persistent greeting <audio> element. Every harvested gesture
 * blesses it with the real silent clip, so when GreetingService later plays
 * the actual greeting on this SAME element, Safari's per-element rule is
 * already satisfied.
 */
export function getGreetingAudioElement() {
    if (!_blessedEl) {
        _blessedEl = new Audio();
        _blessedEl.preload = "auto";
        try {
            _blessedEl.setAttribute("playsinline", "");
        } catch (_) {}
    }
    return _blessedEl;
}

/**
 * A real greeting is about to own the element: stop any silent-clip bless
 * in flight and suspend further blessing until released. (Pausing the
 * silent clip is NOT pausing speech — the claim exists precisely so a
 * gesture can never swap src under live audio.)
 */
export function claimGreetingAudioElement() {
    const el = getGreetingAudioElement();
    _blessedElClaimed = true;
    try {
        el.pause();
    } catch (_) {}
    return el;
}

export function releaseGreetingAudioElement() {
    _blessedElClaimed = false;
}

/**
 * Play the real silent clip on the persistent element. Resolves true if the
 * browser allowed it (element is now blessed / autoplay is permitted).
 */
export function blessGreetingAudioNow() {
    if (_blessedElClaimed) return Promise.resolve(true);
    try {
        const el = getGreetingAudioElement();
        el.src = SILENT_AUDIO_URI;
        const p = el.play();
        if (p && typeof p.then === "function") {
            return p.then(
                () => {
                    if (!_blessedElClaimed) {
                        try {
                            el.pause();
                        } catch (_) {}
                        try {
                            el.currentTime = 0;
                        } catch (_) {}
                    }
                    return true;
                },
                () => false,
            );
        }
        return Promise.resolve(true);
    } catch (_) {
        return Promise.resolve(false);
    }
}

/**
 * The unlock work itself. MUST be called synchronously inside a user
 * gesture to count on Safari:
 *   1) resume (creating if needed) the realtime-TTS AudioContext — the
 *      dynamic-greeting / chat speech path;
 *   2) play a throwaway REAL silent clip — document-level media engagement;
 *   3) bless the persistent greeting element — Safari's per-element rule
 *      for the GreetingService path.
 */
export function unlockAudioNow() {
    // Public compat flag (stage.js maintains the same one; nothing gates on
    // it anymore, but keep it truthful for debugging).
    try {
        window.__audioGestureUnlocked = true;
    } catch (_) {}

    try {
        const rtts = _findTTSEngine();
        if (rtts) {
            if (
                !rtts.audioCtx &&
                typeof rtts._ensureAudioContext === "function"
            ) {
                // Creates the context SYNCHRONOUSLY (so it inherits this
                // gesture's activation), then resumes — the exact call every
                // playback path makes anyway. Fire-and-forget.
                rtts._ensureAudioContext().catch(() => {});
            } else if (
                rtts.audioCtx &&
                typeof rtts.audioCtx.resume === "function"
            ) {
                rtts.audioCtx.resume().catch(() => {});
            }
        }
    } catch (_) {}

    try {
        const a = new Audio(SILENT_AUDIO_URI);
        a.preload = "auto";
        const p = a.play();
        if (p && typeof p.then === "function") {
            p.then(() => {
                try {
                    a.pause();
                } catch (_) {}
            }).catch(() => {});
        }
    } catch (_) {}

    blessGreetingAudioNow();
}

/** True once a verified user activation has run the in-stack unlock. */
export function hasUnlockGesture() {
    return _gestureSeen;
}

// Every event type that can carry user activation. pointerdown alone does
// not activate on iOS (the paired pointerup/touchend does), so activation
// is verified via navigator.userActivation before a gesture "counts".
const EARLY_UNLOCK_EVENTS = [
    "pointerdown",
    "pointerup",
    "touchend",
    "keydown",
    "click",
];

function onEarlyGesture(e) {
    if (_committed) return;
    // Do the unlock work IN the gesture stack — resume + silent clips only
    // stick while the activation is live.
    unlockAudioNow();
    let active = true;
    try {
        const ua = navigator.userActivation;
        if (ua) active = !!(ua.hasBeenActive || ua.isActive);
        else active = e.type !== "pointerdown";
    } catch (_) {}
    if (!active) return;
    _gestureSeen = true;
    if (_armed) {
        const start = _armed;
        _armed = null;
        hideSpeakPill();
        try {
            // Runs synchronously inside the gesture handler so any play()
            // in the armed starter carries this user activation.
            start();
        } catch (err) {
            console.error("[GreetingUnlock] armed greeting start failed:", err);
        }
    }
}

/**
 * Begin harvesting page-wide gestures. Installed at /lab boot (main.js) so
 * taps during the long model load already unlock audio; armGreetingStart()
 * auto-installs on surfaces that never booted the kit.
 */
export function installGreetingAudioUnlock() {
    if (_installed || _committed) return;
    _installed = true;
    EARLY_UNLOCK_EVENTS.forEach((type) => {
        try {
            window.addEventListener(type, onEarlyGesture, {
                capture: true,
                passive: true,
            });
        } catch (_) {}
    });
}

/**
 * The greeting has committed (voice started, or honestly fell back to
 * text): tear down the harvest listeners and the pill so mid-speech clicks
 * can never re-bless the element or touch the audio context again.
 */
export function commitGreetingAudioUnlock() {
    _committed = true;
    _armed = null;
    hideSpeakPill();
    if (!_installed) return;
    _installed = false;
    EARLY_UNLOCK_EVENTS.forEach((type) => {
        try {
            window.removeEventListener(type, onEarlyGesture, { capture: true });
        } catch (_) {}
    });
}

/**
 * Hold a greeting start until the first gesture anywhere: `startFn` runs
 * SYNCHRONOUSLY inside that gesture's handler (so in-stack play() is
 * allowed). Returns false — caller must not wait — if the kit has already
 * committed. Optionally shows the minimal pill.
 */
export function armGreetingStart(
    startFn,
    { showPill = false, characterName = null } = {},
) {
    if (_committed || typeof startFn !== "function") return false;
    installGreetingAudioUnlock();
    _armed = startFn;
    if (showPill) showSpeakPill(characterName);
    return true;
}

/**
 * Bounded audible-autoplay probe (the /learn real-silent-clip contract):
 * resume/create the TTS AudioContext and bless the persistent element,
 * racing a hard ceiling (a blocked Chrome leaves resume() pending forever).
 * `audible` is the verdict for the path that will actually speak: the
 * Web-Audio context when one exists, the element otherwise.
 */
export async function probeGreetingAutoplay(rtts = null, ceilingMs = 2500) {
    const blocked = { ctxRunning: false, elementOk: false, audible: false };
    const attempt = (async () => {
        const engine = rtts || _findTTSEngine();
        let ctxRunning = null;
        if (engine) {
            try {
                if (
                    !engine.audioCtx &&
                    typeof engine._ensureAudioContext === "function"
                ) {
                    await Promise.race([
                        engine._ensureAudioContext().catch(() => {}),
                        new Promise((r) => setTimeout(r, 900)),
                    ]);
                } else if (
                    engine.audioCtx &&
                    engine.audioCtx.state !== "running" &&
                    typeof engine.audioCtx.resume === "function"
                ) {
                    await Promise.race([
                        Promise.resolve(engine.audioCtx.resume()).catch(
                            () => {},
                        ),
                        new Promise((r) => setTimeout(r, 900)),
                    ]);
                }
            } catch (_) {}
            ctxRunning = !!(
                engine.audioCtx && engine.audioCtx.state === "running"
            );
        }
        const elementOk = await blessGreetingAudioNow();
        const audible = ctxRunning === null ? !!elementOk : !!ctxRunning;
        return { ctxRunning: !!ctxRunning, elementOk: !!elementOk, audible };
    })();
    try {
        return await Promise.race([
            attempt,
            new Promise((r) =>
                setTimeout(() => r({ ...blocked, timedOut: true }), ceilingMs),
            ),
        ]);
    } catch (_) {
        return blocked;
    }
}

// ==============================
// The minimal "tap" pill
// ==============================
// Shown ONLY when playback was refused with zero interaction — the
// last-resort floor, in the spirit of the /learn "Tap to begin" pill.
// Tapping ANYWHERE (not just the pill) starts the voice; the pill leaves as
// soon as speech commits. Styles are JS-injected because /lab's static CSS
// is 30-day cached. Per the stage-tapstart precedent the pill is NOT hidden
// in ?demo=1 capture: under blocked autoplay it IS the shot's start control,
// and it leaves before speech begins.

function ensurePillStyles() {
    if (document.getElementById("lab-speak-pill-styles")) return;
    const style = document.createElement("style");
    style.id = "lab-speak-pill-styles";
    style.textContent = `
.lab-speak-pill {
  position: fixed; left: 50%; transform: translateX(-50%);
  bottom: calc(clamp(96px, 15vh, 180px) + env(safe-area-inset-bottom, 0px));
  z-index: 9500;
  display: inline-flex; align-items: center; gap: 10px;
  padding: 14px 26px; border: none; border-radius: 999px;
  font: 600 15px 'Manrope', sans-serif; cursor: pointer;
  background: linear-gradient(135deg, #d4a574, #c8894a); color: #14100b;
  box-shadow: 0 16px 30px -12px rgba(168, 116, 47, 0.7);
  animation: labSpeakPillBreathe 2.4s ease-in-out infinite;
}
.lab-speak-pill:hover { filter: brightness(1.05); }
.lab-speak-pill.is-leaving { opacity: 0; transition: opacity 0.3s ease; }
@keyframes labSpeakPillBreathe {
  0%, 100% { transform: translateX(-50%) scale(1); }
  50% { transform: translateX(-50%) scale(1.045); }
}
.lab-speak-pill .lab-speak-pill-dot {
  width: 8px; height: 8px; border-radius: 50%; flex: 0 0 auto;
  background: rgba(20, 16, 11, 0.85);
  animation: labSpeakPillPulse 1.2s ease-in-out infinite;
}
@keyframes labSpeakPillPulse {
  0%, 100% { opacity: 0.35; transform: scale(0.85); }
  50% { opacity: 1; transform: scale(1.15); }
}
`;
    document.head.appendChild(style);
}

export function showSpeakPill(characterName = null) {
    if (_committed || _pillEl) return;
    if (!document.body) return;
    ensurePillStyles();
    const first = characterName
        ? String(characterName).trim().split(/\s+/)[0]
        : null;
    const label = first ? `Tap to hear ${first}` : "Tap to hear their voice";
    const pill = document.createElement("button");
    pill.type = "button";
    pill.className = "lab-speak-pill";
    pill.setAttribute(
        "aria-label",
        `Tap anywhere to hear ${first || "the legend"} speak`,
    );
    const dot = document.createElement("span");
    dot.className = "lab-speak-pill-dot";
    dot.setAttribute("aria-hidden", "true");
    pill.appendChild(dot);
    pill.appendChild(document.createTextNode(label));
    // Belt-and-braces: the page-wide capture listeners already treat this
    // click as the unlock gesture; a direct handler covers any exotic
    // stopPropagation along the way.
    pill.addEventListener("click", (e) => onEarlyGesture(e));
    document.body.appendChild(pill);
    _pillEl = pill;
}

export function hideSpeakPill() {
    const pill = _pillEl;
    if (!pill) return;
    _pillEl = null;
    try {
        pill.classList.add("is-leaving");
        setTimeout(() => {
            try {
                pill.remove();
            } catch (_) {}
        }, 320);
    } catch (_) {
        try {
            pill.remove();
        } catch (_) {}
    }
}
